import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Lock, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import api from '../utils/api';
import { setToken, setUser } from '../utils/auth';

const LoginPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post('/auth/login', formData);
      const { user, token } = response.data;

      setToken(token);
      setUser(user);

      toast.success(
        i18n.language === 'ar' ? 'تم تسجيل الدخول بنجاح' : 'Login successful'
      );

      // Redirect based on user role and type
      console.log('🔍 User data:', user);
      console.log('🔍 Role:', user.role);
      console.log('🔍 Client Type:', user.client_type);
      console.log('🔍 User Type:', user.user_type);
      
      if (user.role === 'client') {
        // Check client type for proper routing
        if (user.client_type === 'business' || user.user_type === 'business') {
          console.log('✅ Redirecting to /business/dashboard');
          navigate('/business/dashboard');
        } else {
          console.log('✅ Redirecting to /client/dashboard');
          navigate('/client/dashboard');
        }
      } else if (user.role === 'admin') {
        console.log('✅ Redirecting to /admin');
        navigate('/admin');
      } else if (user.role === 'lawyer') {
        // Check if staff or lawyer
        if (user.user_type === 'staff') {
          console.log('✅ Redirecting to /staff/dashboard');
          navigate('/admin/labor-intake');
        } else {
          console.log('✅ Redirecting to /lawyer');
          navigate('/lawyer');
        }
      } else {
        console.log('✅ Redirecting to /dashboard');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
          (i18n.language === 'ar' ? 'فشل تسجيل الدخول' : 'Login failed')
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden" data-testid="login-page">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-wazin-navy/5 via-gray-50 to-wazin-gold/5 -z-20" />
      
      {/* Watermark - Wazin Logo */}
      <div className="absolute inset-0 flex items-center justify-center -z-10 pointer-events-none">
        <img 
          src="/images/wazin-logo.png" 
          alt="Wazin Watermark" 
          className="w-[500px] md:w-[700px] lg:w-[900px] h-auto object-contain opacity-[0.07] select-none"
          style={{ filter: 'grayscale(10%)' }}
        />
      </div>
      
      <Card className="w-full max-w-md glass shadow-2xl border-2 border-wazin-gold/20 backdrop-blur-xl bg-white/95" data-testid="login-form-card">
        <CardHeader className="text-center space-y-4 pb-6">
          {/* Wazin Icon */}
          <div className="mx-auto w-20 h-20 rounded-full bg-gradient-to-br from-wazin-gold to-yellow-600 flex items-center justify-center shadow-lg">
            <img 
              src="/wazin-icon-gold.png" 
              alt="Wazin Icon" 
              className="w-12 h-12 object-contain filter brightness-0 invert"
            />
          </div>
          <div>
            <CardTitle className="text-3xl font-bold text-wazin-navy">
              {t('loginTitle')}
            </CardTitle>
            <CardDescription className="text-lg mt-2 text-gray-600">
              {i18n.language === 'ar'
                ? 'مرحباً بعودتك إلى وازن'
                : 'Welcome back to Wazin'
              }
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-base">
                {t('email')}
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-secondary" />
                <Input
                  id="email"
                  type="email"
                  placeholder={i18n.language === 'ar' ? 'البريد الإلكتروني' : 'your@email.com'}
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="pl-10 input"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-base">
                {t('password')}
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-secondary" />
                <Input
                  id="password"
                  type="password"
                  placeholder="********"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="pl-10 input"
                  required
                  data-testid="password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-wazin-gold to-yellow-600 hover:from-wazin-gold/90 hover:to-yellow-600/90 text-wazin-navy font-bold text-lg py-6 shadow-lg hover:shadow-xl transition-all duration-300"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t('loading')}
                </>
              ) : (
                t('login')
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-600">
              {t('dontHaveAccount')}{' '}
              <Link to="/register" className="text-wazin-gold font-semibold hover:text-wazin-navy transition-colors" data-testid="register-link">
                {t('register')}
              </Link>
            </p>
          </div>

          <div className="mt-4 text-center space-y-2">
            <div>
              <Link to="/" className="text-sm text-gray-500 hover:text-wazin-gold transition-colors" data-testid="back-home-link">
                {i18n.language === 'ar' ? 'العودة للرئيسية' : 'Back to Home'}
              </Link>
            </div>
            <div className="pt-2 border-t border-gray-200">
              <Link 
                to="/clear-cache" 
                className="text-xs text-blue-600 hover:text-blue-800 transition-colors inline-flex items-center gap-1"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                {i18n.language === 'ar' ? 'مسح الذاكرة المؤقتة' : 'Clear Cache'}
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;
